import { useState, useEffect, useCallback, useRef } from 'react';
import { InsightsFeedService, InsightsFeedPage } from '../../../services/insightsFeedService';

interface UseInsightsFeedReturn {
  items: InsightsFeedPage['items'];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  refresh: () => Promise<void>;
  loadMore: () => Promise<void>;
  onIndexChange: (index: number) => void;
}

// Prefetch configuration
const PREFETCH_THRESHOLD = 5; // Start prefetching when this many items from end

/**
 * Hook for managing insights feed data and pagination
 * Uses FlatList's built-in windowing - no custom sliding window
 * Handles loading, error states, and infinite scrolling
 */
export const useInsightsFeed = (): UseInsightsFeedReturn => {
  // All fetched items - FlatList will handle rendering optimization
  const [items, setItems] = useState<InsightsFeedPage['items']>([]);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  
  // Track current index for prefetching
  const currentIndex = useRef(0);
  
  // Track seen IDs to avoid duplicates (Set for O(1) lookup)
  const seenIds = useRef<Set<string>>(new Set());
  
  // Track if prefetch is in progress
  const isPrefetching = useRef(false);

  /**
   * Handle index change (called from screen when user scrolls)
   * Just track index and trigger prefetch - let FlatList handle rendering
   */
  const onIndexChange = useCallback((index: number) => {
    currentIndex.current = index;
    
    // Mark current insight as seen (persistently)
    if (index >= 0 && index < items.length) {
      const currentInsight = items[index];
      
      if (currentInsight && !seenIds.current.has(currentInsight.insight.id)) {
        seenIds.current.add(currentInsight.insight.id);
        // Mark as seen in persistent storage (fire and forget)
        InsightsFeedService.markInsightsAsSeen([currentInsight.insight.id]);
      }
    }
    
    // Check if we should prefetch next page
    if (hasMore && !isPrefetching.current && index >= items.length - PREFETCH_THRESHOLD) {
      console.log(`🔄 Prefetching next page (at item ${index}/${items.length})...`);
      isPrefetching.current = true;
      
      InsightsFeedService.getInsightsFeedPage(10, cursor)
        .then(result => {
          if (result.success && result.data) {
            // Filter out duplicates
            const newItems = result.data.items.filter(
              item => !seenIds.current.has(item.insight.id)
            );
            
            // Add new items
            if (newItems.length > 0) {
              // Don't add to seenIds yet - only when user actually views them!
              
              setItems(prev => [...prev, ...newItems]);
              setHasMore(result.data!.hasMore);
              setCursor(result.data!.nextCursor);
              
              console.log(`✅ Prefetched ${newItems.length} new items (total: ${items.length + newItems.length})`);
            } else {
              console.log('⚠️ No new unique items in prefetch');
            }
          }
        })
        .catch(err => {
          console.warn('Prefetch failed (non-critical):', err);
        })
        .finally(() => {
          isPrefetching.current = false;
        });
    }
  }, [hasMore, cursor, items]);

  /**
   * Load initial page of insights
   */
  const loadInitialPage = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      seenIds.current.clear();

      const result = await InsightsFeedService.getInsightsFeedPage();

      if (result.success && result.data) {
        // Don't add to seenIds yet - only when user actually views them!
        
        if (result.data.items.length === 0) {
          setError('No new insights available. You\'ve seen everything! Come back later for fresh content.');
          setItems([]);
          setHasMore(false);
        } else {
          setItems(result.data.items);
          setHasMore(result.data.hasMore);
          setCursor(result.data.nextCursor);
          
          currentIndex.current = 0;
          
          console.log(`✅ Loaded ${result.data.items.length} initial items`);
        }
      } else {
        setError(result.error || 'Failed to load insights');
        setItems([]);
      }
    } catch (err) {
      console.error('Error loading insights feed:', err);
      setError(err instanceof Error ? err.message : 'Failed to load insights');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Refresh the feed (pull-to-refresh)
   */
  const refresh = useCallback(async () => {
    // Clear cache to force fresh data (but keep seen IDs!)
    InsightsFeedService.clearCache();
    // Don't clear seenIds - we want to remember what user has seen
    setCursor(undefined);
    currentIndex.current = 0;
    isPrefetching.current = false;
    await loadInitialPage();
  }, []); // loadInitialPage is now stable

  /**
   * Load more items (manual trigger, e.g., for end reached)
   * Note: This is mostly handled by prefetch, but kept for compatibility
   */
  const loadMore = useCallback(async () => {
    if (!hasMore || loading || isPrefetching.current) {
      return;
    }

    try {
      setLoading(true);

      const result = await InsightsFeedService.getInsightsFeedPage(10, cursor);

      if (result.success && result.data) {
        // Filter out duplicates
        const newItems = result.data.items.filter(
          item => !seenIds.current.has(item.insight.id)
        );
        
        if (newItems.length > 0) {
          // Don't add to seenIds yet - only when user actually views them!
          
          setItems(prev => [...prev, ...newItems]);
          setHasMore(result.data.hasMore);
          setCursor(result.data.nextCursor);
          
          console.log(`✅ Loaded ${newItems.length} more items`);
        }
      }
    } catch (err) {
      console.error('Error loading more insights:', err);
      // Don't show error for loadMore, just stop loading
    } finally {
      setLoading(false);
    }
  }, [hasMore, loading, cursor]);

  // Load initial page on mount (only once)
  useEffect(() => {
    loadInitialPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally empty - only run on mount

  return {
    items,
    loading,
    error,
    hasMore,
    refresh,
    loadMore,
    onIndexChange,
  };
};

